import { clientDevMock } from '@/devtools/mocks/clientDevMock';
import { pkwInputDevMock } from '@/devtools/mocks/pkwInputDevMock';
import { resInputDevMock } from '@/devtools/mocks/resInputDevMock';
import { nutzInputDevMock } from '@/devtools/mocks/nutzInputDevMock';
import { nutzv2InputDevMock } from '@/devtools/mocks/nutzv2InputDevMock';
import { useClientStore, useWritableClients } from '@/features/Clients';
import { useFeaturesStore } from '@/features/Features';
import type { FeatureItem } from '@/features/Features';
import type { Sparte } from '@/features/Global/types';
import { useInputStore } from '@/features/Sparten';
import { isPkwSparte } from '@/features/Sparten/shared/kfz/isPkwSparte';
import { useTariffStore } from '@/features/Tariffs/store';

export interface DevtoolHelper {
  selectAllFeatures: () => Promise<void>;
  loadTariffs: () => Promise<string[]>;
  showContract: () => Promise<void>;
  resetState: () => Promise<void>;
}

const baseResetHelper = (): Promise<void> =>
  new Promise<void>((resolve) => {
    resolve();
  });

const baseLoadTariffsHelper = (): Promise<string[]> =>
  new Promise<string[]>((resolve) => {
    const tariffStore = useTariffStore();

    tariffStore
      .loadTariffs()
      .catch(() => {
        // remove when redis response is working again and replace with awaits
      })
      .finally(() => {
        tariffStore.toggleSelection(tariffStore.activeTariffs[0]);
        tariffStore.toggleSelection(tariffStore.activeTariffs[1]);

        useFeaturesStore()
          .loadTariffFeatures()
          .then(() => {
            resolve(tariffStore.selection);
          });
      });
  });

const baseShowContractHelper = (): Promise<void> =>
  new Promise<void>((resolve) => {
    resolve();
  });

export const pfeDevtoolHelpers: DevtoolHelper = {
  selectAllFeatures: () =>
    new Promise<void>((resolve) => {
      const { vnProxy } = useWritableClients();
      if (vnProxy.value !== undefined) {
        const today = new Date();
        vnProxy.value.geburtsdatum = new Date(
          today.setDate(today.getDate() - 7),
        );
      }
      useInputStore().vorversicherung = 'nein';

      const featuresStore = useFeaturesStore();
      if (featuresStore.data.length > 0) {
        featuresStore.data.forEach((group) => {
          group.items.forEach((item: FeatureItem) => {
            item.value = 1;
          });
        });
      }

      resolve();
    }),

  loadTariffs: baseLoadTariffsHelper,

  showContract: baseShowContractHelper,

  resetState: baseResetHelper,
};

export const pkwDevtoolHelpers: DevtoolHelper = {
  selectAllFeatures: () =>
    new Promise<void>((resolve) => {
      useInputStore().$state = pkwInputDevMock;

      useClientStore().entities[0].data = clientDevMock;

      resolve();
    }),

  loadTariffs: baseLoadTariffsHelper,

  showContract: baseShowContractHelper,

  resetState: baseResetHelper,
};

export const resDevtoolHelpers: DevtoolHelper = {
  selectAllFeatures: () =>
    new Promise<void>((resolve) => {
      useInputStore().$state = resInputDevMock;

      useClientStore().entities[0].data = clientDevMock;

      resolve();
    }),

  loadTariffs: baseLoadTariffsHelper,

  showContract: baseShowContractHelper,

  resetState: baseResetHelper,
};

export const nutzDevtoolHelpers: DevtoolHelper = {
  selectAllFeatures: () =>
    new Promise<void>((resolve) => {
      useInputStore().$state = nutzInputDevMock;

      useClientStore().entities[0].data = clientDevMock;

      resolve();
    }),

  loadTariffs: baseLoadTariffsHelper,

  showContract: baseShowContractHelper,

  resetState: baseResetHelper,
};

/**
 * Eigener Helper statt Wiederverwendung von {@see nutzDevtoolHelpers}: `selectAllFeatures` ersetzt
 * den kompletten Store-State, und der V1-Mock traegt Werte, die der nutzv2-Validator ablehnt.
 */
export const nutzv2DevtoolHelpers: DevtoolHelper = {
  selectAllFeatures: () =>
    new Promise<void>((resolve) => {
      useInputStore().$state = nutzv2InputDevMock;

      useClientStore().entities[0].data = clientDevMock;

      resolve();
    }),

  loadTariffs: baseLoadTariffsHelper,

  showContract: baseShowContractHelper,

  resetState: baseResetHelper,
};

export const getHelper = (sparte: Sparte): DevtoolHelper => {
  if (sparte === 'pfe') {
    return pfeDevtoolHelpers;
  }

  if (isPkwSparte(sparte)) {
    return pkwDevtoolHelpers;
  }

  if (sparte === 'res') {
    return resDevtoolHelpers;
  }

  // Getrennte Zweige statt isNutzSparte(): die Sparten brauchen unterschiedliche Mocks.
  if (sparte === 'nutz') {
    return nutzDevtoolHelpers;
  }

  if (sparte === 'nutzv2') {
    return nutzv2DevtoolHelpers;
  }

  throw new Error('sparte has no devtool helper');
};
